import type { GitpodAPI } from "@/api";
import { VSCodeIPC } from "@/vscode-ipc";
import { getVSCodeURL } from "@/utils/custom-domain";

const VSCodeServerHelperId = "vscode-server-helper";

type VscodeServerSingleton = {
    iframe: HTMLIFrameElement;
    ipc: VSCodeIPC;
};

let instance: VscodeServerSingleton | null = null;

const resolvedEnvironments = new Set<string>();

/**
 * Returns the singleton VSCodeIPC instance, creating the hidden iframe
 * and IPC channel on first call. Once created, the iframe is never removed
 * from the DOM.
 */
export function getOrCreateVscodeServer(api: GitpodAPI): VscodeServerSingleton {
    if (instance) {
        return instance;
    }

    const vscodeUrl = getVSCodeURL();
    // const vscodeUrl = "http://localhost:7000"; // dev

    const iframe = document.createElement("iframe");
    iframe.id = VSCodeServerHelperId;
    iframe.style.display = "none";
    iframe.src = vscodeUrl;
    iframe.allow = "local-network-access";
    document.body.appendChild(iframe);

    const ipc = new VSCodeIPC(api, iframe, "dark", vscodeUrl);

    instance = { iframe, ipc };
    return instance;
}

/**
 * Marks an environment as resolved. Returns true if it was newly added
 * (i.e. resolve() should be called), false if already seen.
 */
export function markEnvironmentResolved(envId: string): boolean {
    if (resolvedEnvironments.has(envId)) return false;
    resolvedEnvironments.add(envId);
    return true;
}

export function resetVscodeServerSingletonForTests(): void {
    instance?.ipc.dispose();
    instance?.iframe.remove();
    instance = null;
    resolvedEnvironments.clear();
}
